/* HR — Reports (R8 fully wired)
 *
 *  Cross-module aggregate reports.
 *
 *  Endpoints (all GET, read-only):
 *    /api/reports/hr/headcount                              (hr/admin)
 *    /api/reports/attendance/monthly?year=&month=&...       (hr/admin)
 *    /api/reports/leave/yearly-usage?year=                  (hr/admin)
 *    /api/reports/expense/monthly?year=&month=              (hr/finance/admin)
 *    /api/reports/payroll/department-summary?year=&month=   (hr/finance/admin)
 *    /api/reports/gl/trial-balance?as_of=                   (finance/admin)
 *    /api/reports/gl/income-statement?from_date=&to_date=   (finance/admin)
 *    /api/reports/gl/balance-sheet?as_of=                   (finance/admin)
 */

/* Expressive shell only (plan-expressive-inner-pages.md §2(c) page CSS): the ≤1479px
 * horizontal report nav gets a borderless white card instead of the Bento outlined strip.
 * No-op under Bento (GxCore.injectCss returns false). */
if (window.GxCore) window.GxCore.injectCss('gx-css-p-reports', String.raw`
html[data-ui-shell="expressive"] .va2-reports-layout .va2-reports-nav{background:var(--gx-card)!important;border:0!important;border-radius:16px!important}
html[data-ui-shell="expressive"] .va2-reports-layout .va2-reports-section-title{border-right-color:var(--gx-olv)!important}
html[data-ui-shell="expressive"] .va2-reports-layout .va2-reports-nav button:hover{background-image:linear-gradient(var(--gx-state-hover),var(--gx-state-hover))}
`);

/* Small helpers ───────────────────────── */

function fmtHours(mins) {
  if (!mins) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function HBar({ value, max, color, label, right }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 90px', gap: 12, alignItems: 'center', padding: '6px 0' }}>
      <span style={{ fontSize: FontSizes.trading_md, color: HR.ink }}>{label}</span>
      <div style={{ height: 8, background: HR.ruleSoft, borderRadius: uiPick(4, GxT.radius.pill), overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || HR.accent, transition: 'width 200ms' }} />
      </div>
      <span style={{ fontSize: FontSizes.xxs, fontFamily: HR.mono, color: HR.inkSoft, textAlign: 'right' }}>{right}</span>
    </div>
  );
}

function StatCard({ eyebrow, title, italic, hint, accent }) {
  return (
    <div style={uiStyle({
      padding: '20px 24px', background: HR.surface,
      border: `1px solid ${HR.rule}`, borderRadius: 6,
    }, { border: 'none', borderRadius: GxT.radius.card })}>
      <div style={uiStyle(
        { fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 4 },
        { fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.p },
      )}>{eyebrow}</div>
      <h3 style={uiStyle({
        fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400, margin: '4px 0',
        letterSpacing: '-0.015em', color: accent || HR.ink,
      }, { fontWeight: GxT.weight.title, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' })}>{title}</h3>
      {italic && <div style={uiStyle({ fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: 'italic' }, { fontStyle: 'normal', color: GxT.color.onV })}>{italic}</div>}
      {hint && <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

/* Expressive-only patches shared by the report tables / totals rows below (Bento: same object back). */
function rptTheadStyle(bento) {
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', color: GxT.color.onV });
}
function rptTotalLabelStyle(bento) {
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.onV });
}

/* ─────────────────────────  HR Headcount  ─────────────── */

function HeadcountReport() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api('GET', '/reports/hr/headcount')
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonText lines={4} />;
  if (!data) return <EmptyState icon="search" title="無資料 / 無權檢視" />;

  const maxDept = Math.max(1, ...data.by_department.map(d => d.count));
  const maxType = Math.max(1, ...data.by_employment_type.map(d => d.count));

  return (
    <>
      <div className="rwd-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 22 }}>
        <StatCard eyebrow="Total Active" title={data.total_active} italic="在職員工" />
        <StatCard eyebrow="Departments"   title={data.by_department.length} italic="部門數" />
        <StatCard eyebrow="Status types"  title={data.by_status.length} italic="員工狀態類別" />
      </div>
      <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Card eyebrow="By Department" title="部門人數分佈" italic="Departments">
          {data.by_department.map(d => (
            <HBar key={d.key} value={d.count} max={maxDept} color={HR.accent}
              label={d.label || d.key} right={d.count} />
          ))}
        </Card>
        <Card eyebrow="By Employment Type" title="雇用類型分佈" italic="Type">
          {data.by_employment_type.map(d => (
            <HBar key={d.key} value={d.count} max={maxType} color="#A56A1F"
              label={d.label || d.key} right={d.count} />
          ))}
        </Card>
      </div>
    </>
  );
}

/* ─────────────────────────  Attendance Monthly  ─────── */

function AttendanceMonthlyReport() {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    const d = await api('GET', `/reports/attendance/monthly?year=${year}&month=${month}`).catch(() => null);
    setData(d);
    setLoading(false);
  }, [year, month]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>年月：</span>
        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} style={inputStyle({ width: 110, fontFamily: HR.mono })} />
        <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(parseInt(e.target.value) || month)} style={inputStyle({ width: 80, fontFamily: HR.mono })} />
      </div>
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <>
          <div className="rwd-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
            <StatCard eyebrow="Employees"    title={data.summary.employee_count} italic="統計人數" />
            <StatCard eyebrow="Total Work"   title={fmtHours(data.summary.total_work_minutes)} italic="總工時" />
            <StatCard eyebrow="Overtime"     title={fmtHours(data.summary.total_overtime_minutes)} italic="總加班" accent={HR.accent} />
            <StatCard eyebrow="Late / Early" title={fmtHours(data.summary.total_late_minutes + data.summary.total_early_leave_minutes)} italic="遲到+早退" accent={HR.down} />
          </div>
          <Card padding="0" eyebrow="By Employee" title={`${year}年${month}月出勤明細`} italic={`${data.items.length} 位`}>
            <div className="va2-table-wrapper">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <thead>
                  <tr style={rptTheadStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint })}>
                    <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>員工</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>部門</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>出勤天數</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>總工時</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>加班</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>遲到</th>
                    <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>早退</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(it => (
                    <tr key={it.employee_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '13px 16px 13px 24px', fontWeight: 500 }}>{it.employee_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</td>
                      <td style={{ padding: '13px 16px', color: HR.inkSoft, fontSize: FontSizes.xxs }}>{it.department_name || '—'}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{it.work_days}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtHours(it.total_work_minutes)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: it.overtime_minutes > 0 ? HR.accent : HR.inkFaint }}>{fmtHours(it.overtime_minutes)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: it.late_minutes > 0 ? HR.down : HR.inkFaint }}>{fmtHours(it.late_minutes)}</td>
                      <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right', fontFamily: HR.mono, color: it.early_leave_minutes > 0 ? HR.warn : HR.inkFaint }}>{fmtHours(it.early_leave_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}

/* ─────────────────────────  Leave Yearly Usage  ─────── */

function LeaveUsageReport() {
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api('GET', `/reports/leave/yearly-usage?year=${year}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year]);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>年度：</span>
        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} style={inputStyle({ width: 110, fontFamily: HR.mono })} />
      </div>
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <Card padding="0" eyebrow="By Employee" title={`${year} 年請假使用率`} italic={`${data.employee_count} 位`}>
          {data.items.length === 0 ? (
            <EmptyState icon="calendar" title="無資料" />
          ) : (
            <div style={{ padding: '14px 24px 22px' }}>
              {data.items.map(emp => (
                <div key={emp.employee_id} style={{
                  padding: '14px 0', borderTop: `1px solid ${HR.ruleSoft}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: uiPick(500, GxT.weight.heading) }}>{emp.employee_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</span>
                    <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{emp.department_name || '—'}</span>
                  </div>
                  {emp.balances.length === 0 ? (
                    <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal'), paddingLeft: 12 }}>無 balance 資料</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                      {emp.balances.map(b => {
                        const granted = (b.entitled_days || 0) + (b.adjusted_days || 0);
                        const pct = granted > 0 ? Math.min(100, Math.round((b.used_days / granted) * 100)) : 0;
                        return (
                          <div key={b.leave_type_id} style={uiStyle({
                            padding: 10, background: HR.surfaceAlt, borderRadius: 4,
                          }, { padding: 12, background: GxT.color.s2, borderRadius: GxT.radius.inner })}>
                            <div style={{ fontSize: FontSizes.trading_xl, fontWeight: uiPick(500, GxT.weight.heading), marginBottom: 4 }}>{b.leave_type_name || b.leave_type_code}</div>
                            <div style={{ fontSize: FontSizes.trading_lg, color: uiPick(HR.inkFaint, GxT.color.onV), fontFamily: HR.mono, marginBottom: 6 }}>
                              {(b.used_days || 0).toFixed(1)} / {granted.toFixed(1)} 天
                              {(b.pending_days || 0) > 0 && <> · 待審 {b.pending_days.toFixed(1)}</>}
                            </div>
                            <div style={{ height: 4, background: uiPick(HR.ruleSoft, GxT.color.s4), borderRadius: uiPick(2, GxT.radius.pill), overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: HR.accent }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/* ─────────────────────────  Expense Monthly  ────────── */

// Status code → display tone + label. Mirrors backend enum
// `expense_claim_status` (draft / submitted / approved / paid / rejected).
const EXPENSE_STATUS_TONES = {
  draft:     { label: '草稿',    tone: 'neutral' },
  submitted: { label: '送簽中',  tone: 'warn' },
  approved:  { label: '已核准',  tone: 'accent' },
  paid:      { label: '已核銷',  tone: 'accent' },
  rejected:  { label: '已退回',  tone: 'down' },
};
function ExpenseStatusPill({ code }) {
  const meta = EXPENSE_STATUS_TONES[code] || { label: code || '—', tone: 'neutral' };
  return <Pill tone={meta.tone} dot={meta.tone !== 'neutral'}>{meta.label}</Pill>;
}

function ExpenseMonthlyReport() {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  // 預設只顯示「已核銷」— 月報是給財務對帳用的，未核銷的不算數
  const [status, setStatus] = React.useState('paid');
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    const qs = `year=${year}&month=${month}` + (status ? `&status=${status}` : '');
    api('GET', `/reports/expense/monthly?${qs}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month, status]);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>年月：</span>
        <input type="number" value={year}  onChange={(e) => setYear(parseInt(e.target.value)  || year)}  style={inputStyle({ width: 110, fontFamily: HR.mono })} />
        <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(parseInt(e.target.value) || month)} style={inputStyle({ width: 80,  fontFamily: HR.mono })} />
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginLeft: 8 }}>狀態：</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle({ width: 140 })}>
          <option value="paid">已核銷</option>
          <option value="approved">已核准 (待付款)</option>
          <option value="submitted">送簽中</option>
          <option value="draft">草稿</option>
          <option value="rejected">已退回</option>
          <option value="">全部狀態</option>
        </select>
      </div>
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <>
          <div className="rwd-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <StatCard eyebrow={status === 'paid' ? 'Total Paid' : 'Total Spend'}
                      title={fmtMoney(data.total_amount)}
                      italic={status === 'paid' ? '本月已核銷合計' : '本月合計'}
                      accent={HR.accent} />
            <StatCard eyebrow="Claims" title={data.total_claims} italic="報帳單數" />
            <StatCard eyebrow="Items"  title={data.total_items}  italic="明細筆數" />
          </div>
          <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
            <Card eyebrow="By Category" title="依類別" italic={`${data.by_category.length} 類`} padding="0">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <tbody>
                  {data.by_category.map(c => (
                    <tr key={c.category_id || c.category_code || '0'} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '12px 16px 12px 24px', fontWeight: 500 }}>{c.category_name || c.category_code || '—'}</td>
                      <td style={{ padding: '12px 16px', color: HR.inkFaint, fontFamily: HR.mono, fontSize: FontSizes.trading_lg }}>{c.item_count} 筆</td>
                      <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(c.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card eyebrow="By Employee" title="依員工" italic={`${data.by_employee.length} 位`} padding="0">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <tbody>
                  {data.by_employee.map(e => (
                    <tr key={e.employee_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '12px 16px 12px 24px', fontWeight: 500 }}>{e.employee_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</td>
                      <td style={{ padding: '12px 16px', color: HR.inkFaint, fontSize: FontSizes.trading_lg }}>{e.department_name || '—'}</td>
                      <td style={{ padding: '12px 16px', color: HR.inkFaint, fontFamily: HR.mono, fontSize: FontSizes.trading_lg }}>{e.claim_count} 單</td>
                      <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(e.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {/* 明細：每張單一列，含狀態 / 核銷日 / 核銷備註 */}
          <Card eyebrow="Claim Detail" title="明細" italic={`${(data.by_claim || []).length} 筆`} padding="0">
            {(data.by_claim || []).length === 0 ? (
              <EmptyState icon="search" title="本月無此狀態的報帳單" hint="切換狀態或月份試試。" />
            ) : (
              <div className="va2-table-wrapper">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                  <thead>
                    <tr style={rptTheadStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint })}>
                      <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>單號 / 標題</th>
                      <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>員工</th>
                      <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>狀態</th>
                      <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>申請日</th>
                      <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>核銷日</th>
                      <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>金額</th>
                      <th style={{ padding: '11px 24px 11px 16px', textAlign: 'left',  fontWeight: 500 }}>核銷備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.by_claim || []).map(cl => (
                      <tr key={cl.claim_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                        <td style={{ padding: '12px 16px 12px 24px' }}>
                          <div style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_xl, color: HR.inkSoft }}>{cl.claim_no || `#${cl.claim_id}`}</div>
                          <div style={{ fontWeight: 500, marginTop: 2 }}>{cl.title || '—'}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 500 }}>{cl.employee_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</div>
                          {cl.department_name && <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 2 }}>{cl.department_name}</div>}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <ExpenseStatusPill code={cl.status} />
                        </td>
                        <td style={{ padding: '12px 16px', fontFamily: HR.mono, fontSize: FontSizes.trading_xl, color: HR.inkSoft }}>
                          {cl.claim_date || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontFamily: HR.mono, fontSize: FontSizes.trading_xl, color: cl.paid_at ? HR.ink : HR.inkFaint }}>
                          {cl.paid_at ? (cl.paid_at.split('T')[0] || cl.paid_at) : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(cl.total_amount)}</td>
                        <td style={{ padding: '12px 24px 12px 16px', fontSize: FontSizes.xxs, color: cl.remark ? HR.ink : HR.inkFaint, maxWidth: 280 }}>
                          {cl.remark || <span style={{ fontStyle: uiPick('italic', 'normal') }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}

/* ─────────────────────────  Payroll Department  ──────── */

function PayrollDeptReport() {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState('');  // '' = 整年
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    const qs = `year=${year}` + (month ? `&month=${month}` : '');
    api('GET', `/reports/payroll/department-summary?${qs}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month]);

  const csvUrl = month
    ? `/api/payroll/reports/${year}/${parseInt(month, 10)}/csv`
    : null;
  const bankUrl = month
    ? `/api/payroll/reports/${year}/${parseInt(month, 10)}/bank`
    : null;

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: csvUrl ? 8 : 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>年度：</span>
        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} style={inputStyle({ width: 110, fontFamily: HR.mono })} />
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>月份（留空=整年）：</span>
        <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(e.target.value)} placeholder="—" style={inputStyle({ width: 80, fontFamily: HR.mono })} />
      </div>
      {csvUrl && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <a
            href={csvUrl}
            style={uiStyle({
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 4,
              background: HR.surfaceAlt, border: `1px solid ${HR.rule}`,
              fontSize: FontSizes.xxs, fontWeight: 500, color: HR.ink,
              textDecoration: 'none',
            }, {
              // tonal button (mockup .btn.sm.tonal, neutral surface)
              minHeight: 36, padding: '0 16px', borderRadius: GxT.radius.btnSm, border: 'none',
              background: GxT.color.s3, color: GxT.color.on, fontWeight: GxT.weight.label,
            })}
            title="下載薪資明細 CSV（23 欄）"
          >
            <Icon name="download" size={13} stroke={1.8} /> 薪資 CSV
          </a>
          <a
            href={bankUrl}
            style={uiStyle({
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 4,
              background: HR.accentSoft, border: `1px solid ${HR.accent}`,
              fontSize: FontSizes.xxs, fontWeight: 500, color: HR.accent,
              textDecoration: 'none',
            }, {
              minHeight: 36, padding: '0 16px', borderRadius: GxT.radius.btnSm, border: 'none',
              background: GxT.color.sc, color: GxT.color.onSc, fontWeight: GxT.weight.label,
            })}
            title="下載銀行轉帳批次檔（給銀行上傳）"
          >
            <Icon name="download" size={13} stroke={1.8} /> 銀行轉帳檔
          </a>
        </div>
      )}
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <>
          <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20 }}>
            <StatCard eyebrow="Total Gross" title={fmtMoney(data.total_gross_pay)} italic="應發合計" />
            <StatCard eyebrow="Total Net"   title={fmtMoney(data.total_net_pay)}   italic="實領合計" accent={HR.accent} />
          </div>
          <Card padding="0" eyebrow="By Department" title="部門薪資總額" italic={`${data.items.length} 個部門`}>
            <div className="va2-table-wrapper">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <thead>
                  <tr style={rptTheadStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint })}>
                    <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>部門</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>員工數</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>加班費</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>應發</th>
                    <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>實領</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={r.department_id || `no-${i}`} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '13px 16px 13px 24px', fontWeight: 500 }}>{r.department_name || '（未分部門）'}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{r.employee_count}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>{fmtMoney(r.total_overtime_pay)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(r.total_gross_pay)}</td>
                      <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600, color: HR.accent }}>{fmtMoney(r.total_net_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}

/* ─────────────────────────  GL Trial Balance  ──────── */

function TrialBalanceReport() {
  const [asOf, setAsOf] = React.useState(todayStr());
  const [onlyApproved, setOnlyApproved] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api('GET', `/reports/gl/trial-balance?as_of=${asOf}&only_approved=${onlyApproved}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [asOf, onlyApproved]);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>截止日：</span>
        <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={inputStyle({ width: 180 })} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: FontSizes.xxs, cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyApproved} onChange={(e) => setOnlyApproved(e.target.checked)} style={{ accentColor: HR.accent }} />
          僅含已核准分錄
        </label>
      </div>
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <Card padding="0" eyebrow="Trial Balance" title={`截至 ${data.as_of}`} italic={data.is_balanced ? '借貸平衡 ✓' : '⚠ 不平衡'}>
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={rptTheadStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint })}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>科目</th>
                  <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>類別</th>
                  <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>借方</th>
                  <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>貸方</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>餘額</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map(a => (
                  <tr key={a.account_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                    <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                      <strong>{a.account_code}</strong> {a.account_name}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: FontSizes.xxs, color: HR.inkSoft }}>{a.account_type || '—'}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{a.debit_total > 0 ? fmtMoney(a.debit_total) : '—'}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{a.credit_total > 0 ? fmtMoney(a.credit_total) : '—'}</td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(a.balance)}</td>
                  </tr>
                ))}
                <tr style={{ background: HR.surfaceAlt, borderTop: `2px solid ${HR.rule}` }}>
                  <td colSpan={2} style={rptTotalLabelStyle({ padding: '12px 16px', textAlign: 'right', fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint })}>合計</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(data.total_debit)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(data.total_credit)}</td>
                  <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right' }}>
                    {data.is_balanced
                      ? <Pill tone="accent" dot>平衡</Pill>
                      : <Pill tone="down" dot>不平衡</Pill>}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

/* ─────────────────────────  GL Income Statement  ──── */

function IncomeStatementReport() {
  const now = new Date();
  const yearStart = `${now.getFullYear()}-01-01`;
  const [from, setFrom] = React.useState(yearStart);
  const [to, setTo] = React.useState(todayStr());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api('GET', `/reports/gl/income-statement?from_date=${from}&to_date=${to}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [from, to]);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>期間：</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle({ width: 180 })} />
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>~</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle({ width: 180 })} />
      </div>
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <>
          <div className="rwd-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <StatCard eyebrow="Total Revenue"  title={fmtMoney(data.total_revenue)}  italic="總收入" accent={HR.accent} />
            <StatCard eyebrow="Total Expenses" title={fmtMoney(data.total_expenses)} italic="總費用" accent={HR.down} />
            <StatCard eyebrow="Net Income"     title={fmtMoney(data.net_income)}     italic="淨利"   accent={data.net_income >= 0 ? HR.accent : HR.down} />
          </div>
          <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <Card eyebrow="Revenue" title="收入" italic={`${data.revenue.length} 個科目`} padding="0">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <tbody>
                  {data.revenue.map(a => (
                    <tr key={a.account_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '12px 16px 12px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                        <strong>{a.account_code}</strong> {a.account_name}
                      </td>
                      <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 500 }}>{fmtMoney(a.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card eyebrow="Expenses" title="費用" italic={`${data.expenses.length} 個科目`} padding="0">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <tbody>
                  {data.expenses.map(a => (
                    <tr key={a.account_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '12px 16px 12px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                        <strong>{a.account_code}</strong> {a.account_name}
                      </td>
                      <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 500 }}>{fmtMoney(a.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

/* ─────────────────────────  GL Balance Sheet  ─────── */

function BalanceSheetReport() {
  const [asOf, setAsOf] = React.useState(todayStr());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api('GET', `/reports/gl/balance-sheet?as_of=${asOf}`)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [asOf]);

  const Section = ({ title, section, accent }) => (
    <Card eyebrow={title.toUpperCase()} title={title} italic={`${section.accounts.length} 個科目`} padding="0">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
        <tbody>
          {section.accounts.map(a => (
            <tr key={a.account_id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
              <td style={{ padding: '11px 16px 11px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                <strong>{a.account_code}</strong> {a.account_name}
              </td>
              <td style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(a.amount)}</td>
            </tr>
          ))}
          <tr style={{ background: HR.surfaceAlt, borderTop: `2px solid ${HR.rule}` }}>
            <td style={rptTotalLabelStyle({ padding: '12px 16px 12px 24px', fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint })}>合計</td>
            <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600, color: accent }}>{fmtMoney(section.total)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>截止日：</span>
        <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={inputStyle({ width: 180 })} />
      </div>
      {loading ? (
        <SkeletonText lines={4} />
      ) : !data ? <EmptyState icon="search" title="無資料 / 無權檢視" /> : (
        <>
          <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20 }}>
            <StatCard eyebrow="Total Assets" title={fmtMoney(data.assets.total)} italic="資產總計" accent={HR.accent} />
            <StatCard eyebrow="Liab + Equity + NI" title={fmtMoney(data.total_liabilities_and_equity)} italic={data.is_balanced ? '平衡 ✓' : '⚠ 不平衡'}
              accent={data.is_balanced ? HR.accent : HR.down} />
          </div>
          <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
            <Section title="資產 Assets" section={data.assets} accent={HR.accent} />
            <Section title="負債 Liabilities" section={data.liabilities} accent={HR.down} />
          </div>
          <div className="rwd-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <Section title="權益 Equity" section={data.equity} accent="#553C7B" />
            <Card eyebrow="NET INCOME (累計)" title="本期淨利" italic="自所有歷史 revenue - expense"
              padding="0">
              <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                <div style={uiStyle({
                  fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
                  color: data.net_income_to_date >= 0 ? HR.accent : HR.down,
                }, { fontWeight: GxT.weight.title, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' })}>{fmtMoney(data.net_income_to_date)}</div>
                <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 6 }}>
                  簡化版未做正式結帳；視為「期末未分配盈餘」加在 equity 端讓 BS 平衡。
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

/* ─────────────────────────  Page entry  ─────────────── */

/* 每個 report 對應後端 routes_reports.py 的 require_role；
 * 我們用 capability 來表達 — admin 自動含全部，user 持有對應 capability 才看得到。 */
const REPORT_DEFS = [
  { key: 'headcount',  label: 'HR 人力',         section: '人資', icon: 'file-text',    Comp: HeadcountReport,         capability: 'reports.hr' },
  { key: 'attendance', label: '出勤月報',         section: '人資', icon: 'clock',        Comp: AttendanceMonthlyReport, capability: 'reports.hr' },
  { key: 'leave',      label: '請假使用',         section: '人資', icon: 'calendar',     Comp: LeaveUsageReport,        capability: 'reports.hr' },
  { key: 'expense',    label: '報帳月報',         section: '財務', icon: 'dollar-sign',  Comp: ExpenseMonthlyReport,    capability: 'reports.finance' },
  { key: 'payroll',    label: '部門薪資',         section: '財務', icon: 'dollar-sign',  Comp: PayrollDeptReport,       capability: 'reports.finance' },
  { key: 'trial',      label: '試算表',           section: '會計', icon: 'layout',       Comp: TrialBalanceReport,      capability: 'reports.accounting' },
  { key: 'pl',         label: '損益表',           section: '會計', icon: 'trending-up',  Comp: IncomeStatementReport,   capability: 'reports.accounting' },
  { key: 'bs',         label: '資產負債表',       section: '會計', icon: 'layout',       Comp: BalanceSheetReport,      capability: 'reports.accounting' },
];

function canAccess(reportDef, user) {
  return hasCapability(user, reportDef.capability);
}

function ReportsPage() {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [active, setActive] = React.useState('headcount');

  React.useEffect(() => {
    api('GET', '/auth/me').then(setUser).catch(() => setUser({})).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="系統 · Reports" title="報表中心" italic="Cross-module Analytics" />
        <SkeletonTable rows={6} />
      </>
    );
  }

  const accessible = REPORT_DEFS.filter(r => canAccess(r, user));
  if (accessible.length === 0) {
    return (
      <>
        <PageHeader eyebrow="系統 · Reports" title="報表中心" italic="Cross-module Analytics" />
        <EmptyState icon="search" title="無可檢視報表" hint="您目前未被授予任何 reports.* capability，請聯絡 admin 授予 hr / finance 角色以取得對應報表權限。" />
      </>
    );
  }

  const activeDef = accessible.find(r => r.key === active) || accessible[0];
  const Comp = activeDef.Comp;

  // 群組
  const groups = {};
  accessible.forEach(r => {
    if (!groups[r.section]) groups[r.section] = [];
    groups[r.section].push(r);
  });

  return (
    <>
      <PageHeader
        eyebrow="系統 · Reports"
        title="報表中心"
        italic="Cross-module Analytics"
        action={
          <Button
            variant="secondary"
            icon="download"
            onClick={() => window.print()}
          >列印 / 匯出 PDF</Button>
        }
      />
      <div className="va2-reports-layout" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 32 }}>
        <nav className="va2-reports-nav" style={{ position: 'sticky', top: 0, alignSelf: 'start' }}>
          {Object.entries(groups).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 18 }} className="va2-reports-group">
              <div style={uiStyle({
                fontSize: FontSizes.trading_sm, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: HR.inkFaint, padding: '0 12px 8px',
              }, { fontSize: GxT.size.meta, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.onV })} className="va2-reports-section-title">{section}</div>
              {items.map(r => {
                const isActive = r.key === activeDef.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => setActive(r.key)}
                    aria-current={uiPick(undefined, isActive ? 'page' : undefined)}
                    style={uiStyle({
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', borderRadius: 4, fontSize: FontSizes.xs,
                      color: isActive ? HR.ink : HR.inkSoft,
                      background: isActive ? HR.surface : 'transparent',
                      border: isActive ? `1px solid ${HR.rule}` : '1px solid transparent',
                      fontWeight: isActive ? 500 : 400,
                      cursor: 'pointer', fontFamily: HR.sans, textAlign: 'left',
                      marginBottom: 2,
                    }, {
                      // M3 nav item: tonal fill for the current report, no outline
                      minHeight: 40, padding: '0 14px', borderRadius: GxT.radius.btn, border: 'none',
                      color: isActive ? GxT.color.onSc : GxT.color.onV,
                      background: isActive ? GxT.color.sc : 'transparent',
                      fontWeight: isActive ? GxT.weight.title : GxT.weight.medium,
                    })}
                  >
                    <Icon name={r.icon} size={13} />
                    {r.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div>
          <Comp />
        </div>
      </div>
    </>
  );
}

window.ReportsPage = ReportsPage;
